import type React from 'react';
import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@xing-com/button';
import { useLoginState } from '@xing-com/crate-hooks-use-login-state';
import type { ApplicationData } from '@xing-com/crate-jobs-helpers';
import { useExperiment } from '@xing-com/hub';
import { mediaConfined } from '@xing-com/layout-tokens';

import { useApplicationTypeHelpers } from '../../../../hooks';
import { useApplyButtonStateProps } from '../../apply-button-state-context';

import { useApplyButtonDefaultProps } from './use-apply-button-default-props';
import { useApplyButtonEmailApplyProps } from './use-apply-button-email-apply-props';
import { useApplyButtonMessageApplyProps } from './use-apply-button-message-apply-props';
import { useApplyButtonUrlApplyProps } from './use-apply-button-url-apply-props';

type Props = {
  application: ApplicationData;
  isHeaderSticky?: boolean;
  variant?: 'primary' | 'secondary';
};

const Apply = styled(Button)<{ $isHeaderSticky: boolean }>`
  width: 100%;

  @media ${mediaConfined} {
    flex-basis: ${({ $isHeaderSticky }) => ($isHeaderSticky ? 'auto' : '46%')};
  }
`;

export const ApplyButton: React.FC<Props> = ({
  application,
  isHeaderSticky = false,
  variant = 'primary',
}) => {
  const {
    type,
    applyUrl,
    applyEmail,
    contactUserId,
    code,
    jobId,
    prioritized,
    slug,
    title,
    isLoApplyEnabled,
  } = application;

  const { isLoggedIn } = useLoginState();
  const {
    isXingApplication,
    isCustomApplication,
    isEmailApplication,
    isMessageApplication,
    isUrlApplication,
  } = useApplicationTypeHelpers(type);

  const ctaLabelExperiment = useExperiment(
    isLoggedIn ? 'ABACUS-516' : 'ABACUS-528'
  );
  const label = useMemo(() => {
    if (isXingApplication) {
      if (!isLoggedIn && isLoApplyEnabled) {
        if (ctaLabelExperiment === 'B') {
          return 'JOBS_JOB_DETAILS_XING_APPLY_LO_START';
        } else if (ctaLabelExperiment === 'C') {
          return 'JOBS_JOB_DETAILS_XING_APPLY_LO';
        }
      }
      return 'JOBS_JOB_DETAILS_XING_APPLY';
    }
    if (isCustomApplication) {
      if (ctaLabelExperiment === 'B') {
        return 'JOBS_JOB_DETAILS_CUSTOM_APPLY';
      } else if (ctaLabelExperiment === 'C') {
        return 'JOBS_JOB_DETAILS_CUSTOM_APPLY_START';
      }
      return 'JOBS_JOB_DETAILS_XING_APPLY';
    }
    if (isUrlApplication) {
      if (ctaLabelExperiment === 'B') {
        return 'JOBS_JOB_DETAILS_EXTERNAL_APPLY';
      } else if (ctaLabelExperiment === 'C') {
        return 'JOBS_JOB_DETAILS_EXTERNAL_SITE_APPLY';
      }
      return 'JOB_POSTING_APPLY_EXTERNAL_URL';
    }
    return 'JOBS_JOB_DETAILS_APPLY';
  }, [
    isXingApplication,
    isCustomApplication,
    isUrlApplication,
    isLoggedIn,
    isLoApplyEnabled,
    ctaLabelExperiment,
  ]);

  const defaultProps = useApplyButtonDefaultProps(slug);
  const applyFlowProps = useApplyButtonStateProps();
  const messageApplyProps = useApplyButtonMessageApplyProps({
    contactUserId,
    jobId,
  });
  const urlApplyProps = useApplyButtonUrlApplyProps({
    jobId,
    slug,
    applyUrl,
    isPrioritizedJob: prioritized,
  });
  const emailApplyProps = useApplyButtonEmailApplyProps({
    code,
    applyEmail,
    title,
    jobId,
  });

  return (
    <Apply
      variant={variant}
      size="medium"
      $isHeaderSticky={isHeaderSticky}
      {...defaultProps}
      {...(isEmailApplication && emailApplyProps)}
      {...(isMessageApplication && messageApplyProps)}
      {...(isUrlApplication && urlApplyProps)}
      {...((isXingApplication || isCustomApplication) && applyFlowProps)}
    >
      <FormattedMessage id={label} />
    </Apply>
  );
};
